import { useRef, useState } from "react";
import { ImagePlus, Trash2, Sparkles } from "lucide-react";
import { resizeImageFile } from "@/lib/media";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AI_IMAGE_GEN_ENABLED } from "@/lib/featureFlags";
import FirstUseConsent, { useFirstUseConsent } from "./FirstUseConsent";

/**
 * Tile photo control with optional AI symbol generation.
 *   - `value` is the current data-URL (or null)
 *   - `onChange(dataUrl|null)` fires on upload, clear, or AI-generate
 *   - `aiLabel` (optional): when present, shows a "✨ Generate" button that
 *     asks Gemini Nano Banana to render an AAC symbol for that label.
 *   - `profileId` (optional): when present, gates first-use behind a
 *     consent modal that's logged server-side.
 */
export default function ImageUpload({ value, onChange, aiLabel, profileId, aiEnabled = false }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // "camera" | "gallery" | "generate" | null

  const consent = useFirstUseConsent({
    feature: "photos",
    profileId,
    enabled: !!profileId,
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const dataUrl = await resizeImageFile(file, 480, 0.85);
      onChange(dataUrl);
    } catch (err) {
      console.warn("ImageUpload: failed to read/resize file", err?.message || err);
      setError("Could not read that image. Try another file.");
    }
    setBusy(false);
  };

  const generate = async () => {
    if (!aiLabel || !aiLabel.trim()) {
      setError("Type a tile label first so the AI knows what to draw.");
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const { data } = await api.post("/symbols/generate", { label: aiLabel.trim() });
      if (data?.data_url) onChange(data.data_url);
      else setError("The AI didn't return an image. Try a simpler word.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-2" data-testid="image-upload">
      <FirstUseConsent
        feature="photos"
        profileId={profileId}
        open={pendingAction !== null && consent.status === "needed"}
        onAccept={() => {
          consent.markAccepted();
          if (pendingAction === "camera") cameraRef.current?.click();
          else if (pendingAction === "gallery") galleryRef.current?.click();
          else if (pendingAction === "generate") generate();
          setPendingAction(null);
        }}
        onCancel={() => setPendingAction(null)}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          className="pill-btn"
          onClick={() => {
            if (consent.status === "needed") setPendingAction("camera");
            else cameraRef.current?.click();
          }}
          disabled={busy || generating || consent.status === "loading"}
          data-testid="image-camera-btn"
        >
          <ImagePlus size={16} />
          <span>📷 Take a photo</span>
        </button>
        <button
          type="button"
          className="pill-btn"
          onClick={() => {
            if (consent.status === "needed") setPendingAction("gallery");
            else galleryRef.current?.click();
          }}
          disabled={busy || generating || consent.status === "loading"}
          data-testid="image-gallery-btn"
        >
          <ImagePlus size={16} />
          <span>🖼️ Choose from device</span>
        </button>
        {aiLabel !== undefined && !AI_IMAGE_GEN_ENABLED && (
          <button
            type="button"
            className="pill-btn"
            disabled
            data-testid="image-ai-coming-soon"
            title="AI image generation is coming soon"
          >
            <Sparkles size={16} />
            <span>✨ AI images — coming soon</span>
          </button>
        )}
        {aiLabel !== undefined && AI_IMAGE_GEN_ENABLED && aiEnabled && (
          <button
            type="button"
            className="pill-btn gold"
            onClick={() => {
              if (consent.status === "needed") setPendingAction("generate");
              else generate();
            }}
            disabled={busy || generating || !aiLabel?.trim() || consent.status === "loading"}
            data-testid="image-ai-btn"
            title={aiLabel ? `Generate an AAC symbol for "${aiLabel}"` : "Type a label first"}
          >
            <Sparkles size={16} />
            {generating ? "Drawing…" : value ? "Re-draw with AI" : "✨ Generate with AI"}
          </button>
        )}
        {value && (
          <button
            type="button"
            className="pill-btn danger"
            onClick={() => onChange(null)}
            disabled={busy || generating}
            data-testid="image-clear-btn"
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
      </div>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        className="hidden"
        data-testid="image-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid="image-gallery-input"
      />
      {value && (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt=""
            className="w-20 h-20 object-cover rounded-xl border-[3px] border-[color:var(--ink)]"
            data-testid="image-upload-preview"
          />
          <span className="text-sm text-[color:var(--ink-soft)]">
            This photo will show on the tile (instead of the symbol).
          </span>
        </div>
      )}
      {generating && !value && (
        <div className="text-sm text-[color:var(--ink-soft)] flex items-center gap-2" data-testid="image-ai-loading">
          <span className="w-2 h-2 rounded-full bg-[color:var(--ink)] animate-bounce" />
          <span className="w-2 h-2 rounded-full bg-[color:var(--ink)] animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="w-2 h-2 rounded-full bg-[color:var(--ink)] animate-bounce" style={{ animationDelay: "240ms" }} />
          <span>Drawing your custom symbol… can take 5–15 seconds.</span>
        </div>
      )}
      {error && <div className="text-sm text-[color:var(--coral-deep)]">{error}</div>}
    </div>
  );
}
